import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';

import { Spinner } from '@app/shared/ui/spinner';

const cvaButtonVariants = cva(
  [
    'button-cvaButtonVariants',
    'relative inline-flex items-center gap-x-1.5 whitespace-nowrap box-border shrink-0 overflow-hidden',
    'transition-colors',
    'disabled:pointer-events-none',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      isLoading: {
        true: 'only-spinner pointer-events-none',
        false: null,
      },
      variant: {
        blue: [
          'text-white bg-main_blue border border-solid border-main_blue',
          'hover:bg-main_blue hover:text-white',
          'focus-visible:bg-main_blue focus-visible:text-white',
          'active:bg-main_blue active:text-white',
          'disabled:bg-main_blue disabled:text-white',
        ],
        lightBlueOutline: ['text-main_light_blue border-2 border-solid border-main_light_blue'],
        darkBlue: [
          'text-white bg-main_dark_blue border border-solid border-main_dark_blue',
          'hover:bg-main_dark_blue hover:text-white',
          'focus-visible:bg-main_dark_blue focus-visible:text-white',
          'active:bg-main_dark_blue active:text-white',
          'disabled:bg-main_dark_blue disabled:text-white',
        ],
        greenOutline: [
          'text-main_green bg-transparent border-[2px] border-solid border-main_green',
          'hover:bg-transparent hover:text-main_green',
          'focus-visible:bg-transparent focus-visible:text-main_green',
          'active:bg-transparent active:text-main_green',
          'disabled:bg-transparent disabled:text-main_green',
        ],
        outline: [
          'text-general_MI_500_default border border-solid border-general_MI_500_default',
          'hover:border-general_MI_600_hover hover:text-general_MI_600_hover',
          'focus-visible:border-general_MI_600_hover focus-visible:text-general_MI_600_hover',
          'active:border-general_MI_700_pressed active:text-general_MI_700_pressed',
          'disabled:border-general_text_MII_disabled disabled:text-general_text_MII_disabled',
        ],
        primary: [
          'text-general_text_MII_default bg-general_MI_500_default',
          'hover:text-general_text_MII_hover hover:bg-general_MI_600_hover',
          'focus-visible:text-general_text_MII_hover focus-visible:bg-general_MI_600_hover',
          'active:text-general_text_MII_default active:bg-general_MI_700_pressed',
          'disabled:text-general_text_MII_disabled disabled:bg-general_MI_800_disabled',
        ],
        ghost: [
          'text-general_MI_500_default',
          'hover:text-general_MI_600_hover',
          'focus-visible:text-general_MI_600_hover',
          'active:general_MI_700_pressed',
          'disabled:text-general_text_MII_disabled',
        ],
        secondary: 'disabled:text-general_text_MII_disabled',
        secondaryOutline: [
          'border border-solid',
          'disabled:text-general_text_MII_disabled',
          'disabled:border-general_text_MII_disabled',
        ],
      },
      size: {
        text14: 'text-font_ds_title_body_body_14',
        text16: 'text-font_ds_title_button_buttons_16',
        text18: 'text-font_ds_title_body_body_18',
        default: 'h-10 px-4 text-font_ds_title_button_buttons_16',
        semiMedium: 'h-11 px-4 text-font_ds_title_button_buttons_16',
        medium: 'h-12 px-4 text-font_ds_title_button_buttons_16',
        large: 'h-14 px-8 text-font_ds_title_body_body_20',
        xLargeRaleway: 'h-[4.625rem] px-10 text-font_raleway_body_black_24',
      },
      borderRadius: {
        rounded: 'rounded',
        '4xl': 'rounded-[2.5rem]',
        '3xl': 'rounded-3xl',
        '2xl': 'rounded-2xl',
        xl: 'rounded-xl',
        lg: 'rounded-lg',
      },
      isFullWidth: {
        true: 'w-full',
        false: '',
      },
      justify: {
        start: 'justify-start',
        center: 'justify-center',
      },
      isActive: {
        true: null,
        false: null,
      },
      isActiveClickable: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        isActive: true,
        isActiveClickable: false,
        className: ['text-general_MI_500_default', 'cursor-default pointer-events-none'],
      },
      {
        variant: 'secondary',
        isActive: true,
        isActiveClickable: true,
        className: ['text-general_MI_500_default'],
      },
      {
        variant: 'secondary',
        isActive: false,
        className: [
          'text-general_text_MII_secondary_i',
          'hover:text-general_text_MII_secondary_ii',
          'focus-visible:text-general_text_MII_secondary_iii',
          'active:text-general_text_MII_secondary_ii',
        ],
      },
      {
        variant: 'secondaryOutline',
        isActive: true,
        isActiveClickable: false,
        className: [
          'text-general_MI_500_default border-general_MI_500_default',
          'cursor-default pointer-events-none',
        ],
      },
      {
        variant: 'secondaryOutline',
        isActive: true,
        isActiveClickable: true,
        className: ['text-general_MI_500_default border-general_MI_500_default'],
      },
      {
        variant: 'secondaryOutline',
        isActive: false,
        className: [
          'text-general_text_MII_secondary_i',
          'border-general_text_MII_secondary_i',
          'hover:text-general_text_MII_secondary_ii hover:border-general_text_MII_secondary_ii',
          'focus-visible:text-general_text_MII_secondary_iii focus-visible:border-general_text_MII_secondary_iii',
          'active:text-general_text_MII_secondary_ii active:border-general_text_MII_secondary_ii',
        ],
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      isFullWidth: false,
      isActive: false,
      isActiveClickable: false,
      borderRadius: 'lg',
      justify: 'center',
      isLoading: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof cvaButtonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isFullWidth,
      justify = 'center',
      borderRadius = 'lg',
      isLoading = false,
      asChild = false,
      isActive = false,
      isActiveClickable = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={asChild ? undefined : 'button'}
        className={clsx(
          cvaButtonVariants({
            variant,
            size,
            isFullWidth,
            borderRadius,
            justify,
            isActive,
            isActiveClickable,
            isLoading,
          }),
          className,
        )}
        ref={ref}
        {...props}
      >
        <Slottable>{children}</Slottable>
        <Spinner
          data-spinner
          isVisible={isLoading}
          className="flex justify-center items-center absolute inset-0 w-full bg-inherit"
        />
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, cvaButtonVariants };
